import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Users,
  MapPin,
  Building2,
  Settings,
  Plus,
  Edit,
  Trash2,
  Search,
  UserPlus,
  Check,
  Eye,
  EyeOff,
  FileText,
  CalendarDays,
  GitBranch,
  Shapes,
  Download,
  ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDate } from '../lib/dates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAME_REGEX = /^[A-Za-zÁÉÍÓÚáéíóúÑñ][A-Za-zÁÉÍÓÚáéíóúÑñ0-9.,()\-\/ ]{2,120}$/;
const PERSON_NAME_REGEX = /^[A-Za-zÁÉÍÓÚáéíóúÑñ][A-Za-zÁÉÍÓÚáéíóúÑñ ]{2,79}$/;
const ACRONYM_REGEX = /^[A-Z0-9]{2,10}$/;
const TERRITORY_CODE_REGEX = /^[A-Z]{2,6}-[0-9]{1,3}$/;
const BASIC_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeText = (value = '') => value.trim().replace(/\s+/g, ' ');

const normalizeCompare = (value = '') =>
  normalizeText(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const toTitleCase = (value = '') =>
  normalizeText(value)
    .toLowerCase()
    .replace(/\b[\wÁÉÍÓÚáéíóúÑñ().,\-\/]+|[ÁÉÍÓÚáéíóúÑñ().,\-\/\w]+/g, (word) => {
      const lower = word.toLowerCase();
      const specials = { cdmx: 'CDMX' };
      if (specials[lower]) return specials[lower];
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    });

const isValidEmail = (email) => BASIC_EMAIL_REGEX.test(email.trim());

const getPasswordChecks = (password) => ({
  minLength: password.length >= 8,
  upper: /[A-ZÁÉÍÓÚÑ]/.test(password),
  lower: /[a-záéíóúñ]/.test(password),
  number: /\d/.test(password),
});

const isValidUserName = (value) => PERSON_NAME_REGEX.test(normalizeText(value));
const isValidTerritoryName = (value) => {
  const clean = normalizeText(value);
  return clean.length >= 5 && clean.length <= 80 && NAME_REGEX.test(clean);
};
const isValidTerritoryCode = (value) => TERRITORY_CODE_REGEX.test(normalizeText(value).toUpperCase());
const isValidDependencyName = (value) => {
  const clean = normalizeText(value);
  return clean.length >= 5 && clean.length <= 100 && NAME_REGEX.test(clean);
};
const isValidDependencyAcronym = (value) => ACRONYM_REGEX.test(normalizeText(value).toUpperCase());

const isValidCdmxLatLng = (lat, lng) => {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return false;
  return latNum >= 19.0 && latNum <= 19.7 && lngNum >= -99.4 && lngNum <= -98.9;
};

const parseGeoJSON = (value = '') => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const isValidGeoJSONGeometry = (value = '') => {
  const parsed = parseGeoJSON(value);
  if (!parsed || typeof parsed !== 'object') return false;
  const validType = parsed.type === 'Polygon' || parsed.type === 'MultiPolygon';
  return validType && Array.isArray(parsed.coordinates);
};

const PasswordRule = ({ ok, children }) => (
  <div className={`flex items-center gap-2 text-xs ${ok ? 'text-green-700' : 'text-slate-500'}`}>
    <Check className={`w-3.5 h-3.5 ${ok ? 'opacity-100' : 'opacity-30'}`} />
    <span>{children}</span>
  </div>
);

const getSettledData = (result, fallback = []) =>
  result?.status === 'fulfilled' ? result.value?.data || fallback : fallback;

const getDependencyLabelById = (dependencies, id) => {
  const dependency = dependencies.find((d) => String(d.id) === String(id));
  return dependency ? `${dependency.acronym} - ${dependency.name}` : '';
};

const getTerritoryLabelById = (territories, id) => {
  const territory = territories.find((t) => String(t.id) === String(id));
  return territory ? territory.name : '';
};

const TruncatedSelectText = ({ children, placeholder = '' }) => (
  <div className="flex w-full min-w-0 max-w-full items-center overflow-hidden pr-8">
    <span
      className={`block min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-left ${children ? '' : 'text-slate-500'
        }`}
    >
      {children || placeholder}
    </span>
  </div>
);

const toMonthInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getMonthlyReportLimits = () => {
  const today = new Date();

  const maxMonth = toMonthInputValue(today);

  const minDate = new Date(today.getFullYear(), today.getMonth() - 5, 1);
  const minMonth = toMonthInputValue(minDate);

  return { minMonth, maxMonth };
};


const formatDateToYMD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLast8WeeklyRanges = () => {
  const weeks = [];
  const currentDate = new Date();
  const dayOfTheWeek = currentDate.getDay();
  const diff = currentDate.getDate() - (dayOfTheWeek + 1);

  for (let i = 0; i < 8; i += 1) {
    // se crea una instancia nueva de current date para evitar mutar la fecha actual
    const weekStart = new Date(currentDate);
    weekStart.setDate(diff - (i * 7));

    // se crea la fecha de fin a partir de la fecha en que inicia la semana
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    const weekStartYMD = formatDateToYMD(weekStart);
    const weekEndYMD = formatDateToYMD(weekEnd);

    weeks.push({
      value: weekStartYMD,
      start: weekStartYMD,
      end: weekEndYMD,
      label: `${formatDate(weekStartYMD)} a ${formatDate(weekEndYMD)}`,
    });
  }

  return weeks;
};

const getLast6MonthlyOptions = () => {
  const today = new Date();
  const months = [];

  for (let i = 0; i < 6; i += 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const value = `${year}-${month}`;

    const label = date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
    });

    months.push({
      value,
      label: label.charAt(0).toUpperCase() + label.slice(1),
    });
  }

  return months;
};

export const Admin = () => {
  const navigate = useNavigate();
  const { user, token, isAdmin, loading: authLoading } = useAuth();

  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [axes, setAxes] = useState([]);
  const [subaxes, setSubaxes] = useState([]);
  const [alcaldias, setAlcaldias] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [expandedAxes, setExpandedAxes] = useState({});

  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [activitySearch, setActivitySearch] = useState('');

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState('weekly');
  const [selectedWeeklyReferenceDate, setSelectedWeeklyReferenceDate] = useState('');
  const [selectedReportMonth, setSelectedReportMonth] = useState('');
  const [downloadingReport, setDownloadingReport] = useState(false);

const { minMonth, maxMonth } = getMonthlyReportLimits();
const weeklyOptions = useMemo(() => getLast8WeeklyRanges(), []);
const monthlyOptions = useMemo(() => getLast6MonthlyOptions(), []);

  const [territorySearch, setTerritorySearch] = useState('');
  const [territoryAlcaldiaFilter, setTerritoryAlcaldiaFilter] = useState('all');

  const [activityDependencyFilter, setActivityDependencyFilter] = useState('all');
  const [activityTerritoryFilter, setActivityTerritoryFilter] = useState('all');
  const [axisSearch, setAxisSearch] = useState('');
  const [polygonSearch, setPolygonSearch] = useState('');
  const [polygonTerritoryFilter, setPolygonTerritoryFilter] = useState('all');

  const [editUserOpen, setEditUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newDependency, setNewDependency] = useState('');

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'editor',
    dependency_id: '',
    curp: '',
  });


  const [addAxisOpen, setAddAxisOpen] = useState(false);
  const [newAxisData, setNewAxisData] = useState({
    name: '',
    description: '',
    type: 'strategic_line',
    parent_id: ''
  });

  const [addTerritoryOpen, setAddTerritoryOpen] = useState(false);
  const [newTerritory, setNewTerritory] = useState({
    name: '',
    code: '',
    alcaldia_id: '',
    lat: 19.4326,
    lng: -99.1332
  });

  const [addPolygonOpen, setAddPolygonOpen] = useState(false);
  const [newPolygonData, setNewPolygonData] = useState({
    territory_id: '',
    name: '',
    // geojson: '' // pendiente por validar nuevamente con backend
  });

  const [addDependencyOpen, setAddDependencyOpen] = useState(false);
  const [newDependencyData, setNewDependencyData] = useState({
    name: '',
    acronym: '',
    color: '#8B1C4A'
  });


  useEffect(() => {
    if (weeklyOptions.length > 0 && !selectedWeeklyReferenceDate) {
      setSelectedWeeklyReferenceDate(weeklyOptions[0].value);
    }
  }, [weeklyOptions, selectedWeeklyReferenceDate]);


  useEffect(() => {
    if (monthlyOptions.length > 0 && !selectedReportMonth) {
      setSelectedReportMonth(monthlyOptions[0].value);
    }
  }, [monthlyOptions, selectedReportMonth]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [authLoading, isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/users?include_inactive=true`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/activities?status=published`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/axes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subaxes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/alcaldias`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/territories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/territory-polygons`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dependencies`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      setUsers(getSettledData(results[0]));
      setActivities(getSettledData(results[1]));
      setAxes(getSettledData(results[2]));
      setSubaxes(getSettledData(results[3]));
      setAlcaldias(getSettledData(results[4]));
      setTerritories(getSettledData(results[5]));
      setPolygons(getSettledData(results[6]));
      setDependencies(getSettledData(results[7]));


      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount > 0) {
        console.warn('Algunos endpoints no respondieron todavía:', results);
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const toggleAxis = (axisId) => {
    setExpandedAxes((prev) => ({
      ...prev,
      [axisId]: !prev[axisId],
    }));
  };

  const axesWithChildren = useMemo(() => {
    return axes.map((axis) => ({
      ...axis,
      children: subaxes.filter(
        (subaxis) => String(subaxis.axis_id) === String(axis.id)
      ),
    }));
  }, [axes, subaxes]);

  const filteredAxesWithChildren = useMemo(() => {
    return axesWithChildren.filter((axis) => {
      const axisText = `${axis.name || ''} ${axis.description || ''}`.toLowerCase();
      const childText = axis.children
        .map((child) => `${child.name || ''} ${child.description || ''}`)
        .join(' ')
        .toLowerCase();

      const haystack = `${axisText} ${childText}`;
      return !axisSearch || haystack.includes(axisSearch.toLowerCase());
    });
  }, [axesWithChildren, axisSearch]);

  const strategicAxes = useMemo(() => {
    return Array.isArray(axes) ? axes : [];
  }, [axes]);

  const passwordChecks = getPasswordChecks(newUserData.password);
  const passwordValid = Object.values(passwordChecks).every(Boolean);
  const emailValid = isValidEmail(newUserData.email);
  const requiresDependency = newUserData.role === 'editor';

  const userEmailExists = users.some(
    (u) => normalizeCompare(u.email) === normalizeCompare(newUserData.email)
  );

  const canCreateUser =
    isValidUserName(newUserData.name) &&
    !!newUserData.email.trim() &&
    !!newUserData.password &&
    !!newUserData.curp.trim() &&
    emailValid &&
    passwordValid &&
    !userEmailExists &&
    (!requiresDependency || newUserData.dependency_id);

  const territoryNameValid = isValidTerritoryName(newTerritory.name);
  const territoryCodeValid = isValidTerritoryCode(newTerritory.code);
  const territoryLatLngValid = isValidCdmxLatLng(newTerritory.lat, newTerritory.lng);

  const territoryNameExists = territories.some(
    (t) => normalizeCompare(t.name) === normalizeCompare(newTerritory.name)
  );

  const territoryCodeExists = territories.some(
    (t) => normalizeCompare(t.code) === normalizeCompare(newTerritory.code)
  );

  const canCreateTerritory =
    territoryNameValid &&
    territoryCodeValid &&
    !!newTerritory.alcaldia_id &&
    !territoryNameExists &&
    !territoryCodeExists &&
    territoryLatLngValid;

  const dependencyNameValid = isValidDependencyName(newDependencyData.name);
  const dependencyAcronymValid = isValidDependencyAcronym(newDependencyData.acronym);

  const dependencyNameExists = dependencies.some(
    (d) => normalizeCompare(d.name) === normalizeCompare(newDependencyData.name)
  );

  const dependencyAcronymExists = dependencies.some(
    (d) => normalizeCompare(d.acronym) === normalizeCompare(newDependencyData.acronym)
  );

  const canCreateDependency =
    dependencyNameValid &&
    dependencyAcronymValid &&
    !dependencyNameExists &&
    !dependencyAcronymExists &&
    !!newDependencyData.color;

  const canCreateAxis =
    normalizeText(newAxisData.name).length >= 3 &&
    (newAxisData.type === 'strategic_line' || !!newAxisData.parent_id);

  const canCreatePolygon =
    !!newPolygonData.territory_id &&
    normalizeText(newPolygonData.name).length >= 3;

  const filteredTerritories = useMemo(() => {
    return territories.filter((territory) => {
      const alcaldiaName =
        territory.alcaldia ||
        alcaldias.find((a) => String(a.id) === String(territory.alcaldia_id))?.name ||
        '';

      const matchesText =
        !territorySearch ||
        `${territory.name || ''} ${territory.code || ''} ${alcaldiaName}`
          .toLowerCase()
          .includes(territorySearch.toLowerCase());

      const matchesAlcaldia =
        territoryAlcaldiaFilter === 'all' ||
        String(territory.alcaldia_id) === String(territoryAlcaldiaFilter);

      return matchesText && matchesAlcaldia;
    });
  }, [territories, alcaldias, territorySearch, territoryAlcaldiaFilter]);

  const filteredPolygons = useMemo(() => {
    return polygons.filter((polygon) => {
      const territoryName =
        territories.find((t) => String(t.id) === String(polygon.territory_id))?.name || '';

      const matchesText =
        !polygonSearch ||
        `${polygon.id || ''} ${territoryName}`
          .toLowerCase()
          .includes(polygonSearch.toLowerCase());

      const matchesTerritory =
        polygonTerritoryFilter === 'all' ||
        String(polygon.territory_id) === String(polygonTerritoryFilter);

      return matchesText && matchesTerritory;
    });
  }, [polygons, polygonSearch, polygonTerritoryFilter, territories]);





  const getMonthDateRange = (monthValue) => {
    if (!monthValue) return null;

    const [yearStr, monthStr] = monthValue.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);

    if (!year || !month) return null;

    const startDate = `${yearStr}-${monthStr}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${yearStr}-${monthStr}-${String(lastDay).padStart(2, '0')}`;

    return { startDate, endDate };
  };

  const handleDownloadMonthlyReport = async () => {
    try {
      if (!selectedReportMonth) {
        toast.error('Selecciona un mes');
        return;
      }

      if (selectedReportMonth < minMonth || selectedReportMonth > maxMonth) {
        toast.error('Solo puedes descargar reportes de los últimos 6 meses');
        return;
      }

      const range = getMonthDateRange(selectedReportMonth);
      if (!range) {
        toast.error('Mes inválido');
        return;
      }

      setDownloadingReport(true);

      const response = await axios.get(`${API}/reports/monthly/export`, {
        params: {
          start_date: range.startDate,
          end_date: range.endDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type:
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      const filename =
        match?.[1] || `reporte_mensual_${range.startDate}_${range.endDate}.xlsx`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Reporte mensual descargado correctamente');
      setReportDialogOpen(false);
    } catch (error) {
      console.error('Error descargando reporte mensual:', error);
      toast.error('No fue posible descargar el reporte mensual');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDownloadWeeklyReport = async () => {
    try {
      if (!selectedWeeklyReferenceDate) {
        toast.error('Selecciona una semana');
        return;
      }

      setDownloadingReport(true);

      const response = await axios.get(`${API}/reports/weekly/export`, {
        params: {
          reference_date: selectedWeeklyReferenceDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type:
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      const filename =
        match?.[1] || `reporte_semanal_${selectedWeeklyReferenceDate}.xlsx`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Reporte semanal descargado correctamente');
      setReportDialogOpen(false);
    } catch (error) {
      console.error('Error descargando reporte semanal:', error);
      toast.error('No fue posible descargar el reporte semanal');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleUpdateUserRole = async () => {
    if (!selectedUser || !newRole) return;

    if (newRole === 'editor' && !newDependency) {
      toast.error('Selecciona una dependencia para el editor');
      return;
    }

    try {
      await axios.put(
        `${API}/users/${selectedUser.id}/role`,
        {
          role: newRole,
          dependency_id: newRole === 'editor' ? newDependency : newDependency || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Rol actualizado');
      setEditUserOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error al actualizar usuario:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Error al actualizar rol');
    }
  };

  const handleToggleUserStatus = async (userId, isCurrentlyActive) => {
    if (isCurrentlyActive && !window.confirm('¿Estás seguro de desactivar este usuario?')) return;
    try {
      const response = await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data?.message || 'Estado del usuario actualizado');
      fetchData();
    } catch {
      toast.error('Error al actualizar el estado del usuario');
    }
  };

  const handleAddUser = async () => {
    if (!canCreateUser) {
      toast.error('Completa correctamente los campos del usuario');
      return;
    }

    try {
      await axios.post(
        `${API}/users`,
        {
          name: toTitleCase(newUserData.name),
          email: normalizeText(newUserData.email).toLowerCase(),
          password: newUserData.password,
          role: newUserData.role,
          dependency_id:
            newUserData.role === 'editor'
              ? newUserData.dependency_id
              : null,
          curp: normalizeText(newUserData.curp).toUpperCase(),
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Usuario creado exitosamente');
      setAddUserOpen(false);
      setShowPassword(false);
      setNewUserData({
        name: '',
        email: '',
        password: '',
        role: 'editor',
        dependency_id: '',
        curp: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error al crear usuario:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleAddAxis = async () => {
    if (!canCreateAxis) {
      toast.error('Completa correctamente los campos del eje');
      return;
    }

    try {
      if (newAxisData.type === 'strategic_line') {
        await axios.post(
          `${API}/axes`,
          {
            name: normalizeText(newAxisData.name),
            description: normalizeText(newAxisData.description),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/subaxes`,
          {
            name: normalizeText(newAxisData.name),
            description: normalizeText(newAxisData.description),
            axis_id: newAxisData.parent_id,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success('Registro creado');
      setAddAxisOpen(false);
      setNewAxisData({
        name: '',
        description: '',
        type: 'strategic_line',
        parent_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteAxis = async (axisId, type = 'strategic_line') => {
    if (!window.confirm('¿Estás seguro de eliminar este registro?')) return;

    try {
      const url =
        type === 'strategic_line'
          ? `${API}/axes/${axisId}`
          : `${API}/subaxes/${axisId}`;

      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Registro eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleAddTerritory = async () => {
    if (!canCreateTerritory) {
      toast.error('Completa correctamente los campos del territorio');
      return;
    }
    if (territoryNameExists) {
      toast.error('Ya existe un territorio con ese nombre');
      return;
    }
    if (territoryCodeExists) {
      toast.error('Ya existe un territorio con ese código');
      return;
    }

    try {
      await axios.post(
        `${API}/territories`,
        {
          name: toTitleCase(newTerritory.name),
          code: normalizeText(newTerritory.code).toUpperCase(),
          alcaldia_id: newTerritory.alcaldia_id,
          lat: Number(newTerritory.lat),
          lng: Number(newTerritory.lng),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Territorio creado');
      setAddTerritoryOpen(false);
      setNewTerritory({ name: '', code: '', alcaldia_id: '', lat: 19.4326, lng: -99.1332 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear territorio');
    }
  };

  const handleDeleteTerritory = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este territorio?')) return;
    try {
      await axios.delete(`${API}/territories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Territorio eliminado');
      fetchData();
    } catch {
      toast.error('Error al eliminar territorio');
    }
  };

  const handleAddPolygon = async () => {
    if (!canCreatePolygon) {
      toast.error('Completa correctamente los campos del polígono');
      return;
    }

    try {
      await axios.post(
        `${API}/territory-polygons`,
        {
          territory_id: newPolygonData.territory_id,
          name: normalizeText(newPolygonData.name),
          // polygon: JSON.stringify(parseGeoJSON(newPolygonData.geojson)) // pendiente por validar con backend
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Polígono creado');
      setAddPolygonOpen(false);
      setNewPolygonData({
        territory_id: '',
        name: '',
        // geojson: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear polígono');
    }
  };

  const handleDeletePolygon = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este polígono?')) return;
    try {
      await axios.delete(`${API}/territory-polygons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Polígono eliminado');
      fetchData();
    } catch {
      toast.error('Error al eliminar polígono');
    }
  };

  const handleAddDependency = async () => {
    if (!canCreateDependency) {
      toast.error('Completa correctamente los campos de la dependencia');
      return;
    }
    if (dependencyNameExists) {
      toast.error('Ya existe una dependencia con ese nombre');
      return;
    }
    if (dependencyAcronymExists) {
      toast.error('Ya existe una dependencia con ese acrónimo');
      return;
    }

    try {
      await axios.post(
        `${API}/dependencies`,
        {
          ...newDependencyData,
          name: toTitleCase(newDependencyData.name),
          acronym: normalizeText(newDependencyData.acronym).toUpperCase(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Dependencia creada');
      setAddDependencyOpen(false);
      setNewDependencyData({ name: '', acronym: '', color: '#8B1C4A' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear dependencia');
    }
  };

  const handleDeleteDependency = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta dependencia?')) return;
    try {
      await axios.delete(`${API}/dependencies/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dependencia eliminada');
      fetchData();
    } catch {
      toast.error('Error al eliminar dependencia');
    }
  };

  const handleDeleteActivity = async (activityId, title = 'esta actividad') => {
    if (!window.confirm(`¿Estás seguro de eliminar la actividad "${title}"? Esta acción no se puede deshacer.`)) return;

    try {
      await axios.delete(`${API}/activities/${activityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Actividad eliminada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar actividad');
    }
  };

  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );


const getDependencyName = (activity) => {
  if (activity.dependency_name) return activity.dependency_name;

  const dependency = dependencies.find(
    (d) => String(d.id) === String(activity.dependency_id)
  );

  return dependency?.name || '—';
};

const getTerritoryName = (activity) => {
  if (activity.territory_name) return activity.territory_name;

  const territory = territories.find(
    (t) => String(t.id) === String(activity.territory_id)
  );

  return territory?.name || '—';
};

const filteredActivities = useMemo(() => {
  return activities.filter((activity) => {
    const dependencyName = getDependencyName(activity);
    const territoryName = getTerritoryName(activity);

    const matchesText =
      !activitySearch ||
      activity.title?.toLowerCase().includes(activitySearch.toLowerCase()) ||
      activity.description?.toLowerCase().includes(activitySearch.toLowerCase()) ||
      dependencyName.toLowerCase().includes(activitySearch.toLowerCase()) ||
      territoryName.toLowerCase().includes(activitySearch.toLowerCase());

    const matchesDependency =
      activityDependencyFilter === 'all' ||
      String(activity.dependency_id) === String(activityDependencyFilter);

    const matchesTerritory =
      activityTerritoryFilter === 'all' ||
      String(activity.territory_id) === String(activityTerritoryFilter);

    return matchesText && matchesDependency && matchesTerritory;
  });
}, [
  activities,
  activitySearch,
  activityDependencyFilter,
  activityTerritoryFilter,
  dependencies,
  territories,
]);

  const getRoleLabel = (role) => {
    if (role === 'super_admin') return 'Super Admin';
    if (role === 'editor') return 'Editor';
    return 'Usuario';
  };

  const getRoleBadgeClass = (role) => {
    if (role === 'super_admin') return 'bg-[#8B1C4A] text-white';
    if (role === 'editor') return 'bg-[#BC955C] text-black';
    return 'bg-slate-200 text-slate-700';
  };

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E1]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#8B1C4A]/20" />
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E1] py-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
              <Settings className="w-6 h-8 text-[#8B1C4A]" />
              Panel de Administración
            </h1>
            <p className="text-slate-600 mt-2">
              Gestión de usuarios, actividades, ejes, territorios, polígonos y dependencias
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <Badge className="bg-[#8B1C4A] text-white w-fit">Super Admin</Badge>

            <Button
              type="button"
              variant="outline"
              className="border-[#8B1C4A] text-[#8B1C4A] hover:bg-[#8B1C4A]/5 hover:text-[#8B1C4A] active:text-[#8B1C4A]"
              onClick={() => setReportDialogOpen(true)}
            >
              <Download className="w-4 h-4 mr-2" />
              Reporte
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>

            <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
              <DialogContent className="w-[calc(100vw-32px)] max-w-[560px] max-h-[90vh] overflow-y-auto p-0">
                <DialogHeader className="px-5 pt-5 pb-2 sm:px-6 pr-12">
                  <DialogTitle className="text-center sm:text-left">
                    Descargar reporte
                  </DialogTitle>
                  <DialogDescription className="text-center sm:text-left">
                    Selecciona si deseas descargar el reporte semanal o mensual.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 px-5 pb-5 sm:px-6">
                  <div className="space-y-2">
                    <Label>Tipo de reporte</Label>
                    <Select value={reportType} onValueChange={setReportType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una opción" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Reporte semanal</SelectItem>
                        <SelectItem value="monthly">Reporte mensual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {reportType === 'weekly' ? (
                    <div className="space-y-2">
                      <Label>Semana a exportar</Label>
                      <Select
                        value={selectedWeeklyReferenceDate}
                        onValueChange={setSelectedWeeklyReferenceDate}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona una semana" />
                        </SelectTrigger>
                        <SelectContent>
                          {weeklyOptions.map((week) => (
                            <SelectItem key={week.value} value={week.value}>
                              {week.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Se muestran las últimas 8 semanas en formato sábado a sábado. Más adelante esto puede quedar alimentado directamente por backend.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Mes a exportar</Label>
                      <Select
                        value={selectedReportMonth}
                        onValueChange={setSelectedReportMonth}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un mes" />
                        </SelectTrigger>
                        <SelectContent>
                          {monthlyOptions.map((month) => (
                            <SelectItem key={month.value} value={month.value}>
                              {month.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Se muestran los últimos 6 meses disponibles para descarga.
                      </p>
                    </div>
                  )}

                  <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setReportDialogOpen(false)}
                      className="w-full sm:w-auto"
                      disabled={downloadingReport}
                    >
                      Cancelar
                    </Button>

                    <Button
                      type="button"
                      className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                      disabled={
                        downloadingReport ||
                        (reportType === 'weekly' && !selectedWeeklyReferenceDate) ||
                        (reportType === 'monthly' && !selectedReportMonth)
                      }
                      onClick={
                        reportType === 'weekly'
                          ? handleDownloadWeeklyReport
                          : handleDownloadMonthlyReport
                      }
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {downloadingReport ? 'Descargando...' : 'Descargar Excel'}
                    </Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>

          </div>
        </div>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-white border border-[#D4C5A5]/30 flex-wrap h-auto">
            <TabsTrigger value="users" className="flex items-center gap-2"><Users className="w-4 h-4" /> Usuarios</TabsTrigger>
            <TabsTrigger value="activities" className="flex items-center gap-2"><FileText className="w-4 h-4" /> Actividades</TabsTrigger>
            <TabsTrigger value="axes" className="flex items-center gap-2"><GitBranch className="w-4 h-4" /> Ejes</TabsTrigger>
            <TabsTrigger value="territories" className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Territorios</TabsTrigger>
            <TabsTrigger value="polygons" className="flex items-center gap-2"><Shapes className="w-4 h-4" /> Polígonos</TabsTrigger>
            <TabsTrigger value="dependencies" className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Dependencias</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card className="border border-[#D4C5A5]/30 overflow-hidden">
              <CardHeader>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <CardTitle>Gestión de Usuarios</CardTitle>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                    <div className="relative w-full sm:w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Buscar usuario..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#8B1C4A] hover:bg-[#70163A]" data-testid="add-user-btn">
                          <UserPlus className="w-4 h-4 mr-2" />
                          Nuevo Usuario
                        </Button>
                      </DialogTrigger>

                      <DialogContent className="w-[calc(100vw-32px)] max-w-[620px] max-h-[90vh] overflow-y-auto p-0">
                        <DialogHeader className="px-5 pt-5 pb-2 sm:px-6 pr-12">
                          <DialogTitle className="text-center sm:text-left leading-tight break-words">
                            Crear Nuevo Usuario
                          </DialogTitle>
                          <DialogDescription className="text-center sm:text-left">
                            Crea un usuario y asígnale un rol.
                          </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 px-5 pb-5 sm:px-6 min-w-0 max-w-full overflow-x-hidden">
                          <div className="space-y-2">
                            <Label>Nombre completo *</Label>
                            <Input
                              value={newUserData.name}
                              onChange={(e) =>
                                setNewUserData({
                                  ...newUserData,
                                  name: e.target.value.replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ ]/g, '')
                                })
                              }
                              placeholder="Ej: Juan Pérez"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>CURP *</Label>
                            <Input
                              value={newUserData.curp}
                              onChange={(e) =>
                                setNewUserData({
                                  ...newUserData,
                                  curp: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                                })
                              }
                              placeholder="Ej: ABCD010101HDFRNN01"
                            />

                            <a
                              href="https://www.gob.mx/curp/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-[#8B1C4A] hover:underline"
                            >
                              ¿No te sabes tu CURP? Consúltala aquí
                            </a>
                          </div>

                          <div className="space-y-2">
                            <Label>Correo electrónico *</Label>
                            <Input
                              type="email"
                              value={newUserData.email}
                              onChange={(e) =>
                                setNewUserData({
                                  ...newUserData,
                                  email: e.target.value.trim().toLowerCase()
                                })
                              }
                              placeholder="Ej: juan@gmail.com"
                              className={
                                newUserData.email && (!emailValid || userEmailExists)
                                  ? 'border-red-500 focus-visible:ring-red-500'
                                  : ''
                              }
                            />
                            {newUserData.email && !emailValid && (
                              <p className="text-xs text-red-600">
                                Ingresa un correo válido.
                              </p>
                            )}
                            {newUserData.email && userEmailExists && (
                              <p className="text-xs text-red-600">
                                Ya existe un usuario con ese correo.
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label>Contraseña *</Label>
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                value={newUserData.password}
                                onChange={(e) =>
                                  setNewUserData({ ...newUserData, password: e.target.value })
                                }
                                placeholder="Contraseña segura"
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                              <PasswordRule ok={passwordChecks.minLength}>Mínimo 8 caracteres</PasswordRule>
                              <PasswordRule ok={passwordChecks.upper}>Una mayúscula</PasswordRule>
                              <PasswordRule ok={passwordChecks.lower}>Una minúscula</PasswordRule>
                              <PasswordRule ok={passwordChecks.number}>Un número</PasswordRule>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label>Rol</Label>
                            <Select
                              value={newUserData.role}
                              onValueChange={(v) =>
                                setNewUserData({
                                  ...newUserData,
                                  role: v
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="editor">Editor</SelectItem>
                                <SelectItem value="super_admin">Super Admin</SelectItem>
                                <SelectItem value="user">Usuario</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2 min-w-0">
                            <Label>
                              Dependencia asignada {newUserData.role !== 'user' ? '*' : ''}
                            </Label>
                            <Select
                              value={newUserData.dependency_id}
                              onValueChange={(v) =>
                                setNewUserData({ ...newUserData, dependency_id: v })
                              }
                            >
                              <SelectTrigger className="w-full min-w-0 max-w-full overflow-hidden pr-8">
                                <TruncatedSelectText placeholder="Seleccionar dependencia">
                                  {newUserData.dependency_id
                                    ? getDependencyLabelById(dependencies, newUserData.dependency_id)
                                    : ''}
                                </TruncatedSelectText>
                              </SelectTrigger>

                              <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[92vw] sm:max-w-[560px]">
                                {dependencies.map((d) => (
                                  <SelectItem
                                    key={String(d.id)}
                                    value={String(d.id)}
                                    className="max-w-full"
                                    title={`${d.acronym} - ${d.name}`}
                                  >
                                    <span className="block w-full whitespace-normal break-words leading-snug">
                                      {d.acronym} - {d.name}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
                            <Button
                              variant="outline"
                              onClick={() => setAddUserOpen(false)}
                              className="w-full sm:w-auto"
                            >
                              Cancelar
                            </Button>
                            <Button
                              onClick={handleAddUser}
                              className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                              disabled={!canCreateUser}
                            >
                              Crear Usuario
                            </Button>
                          </DialogFooter>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-3">
                  {filteredUsers.map((u) => (
                    <div
                      key={u.id}
                      className="flex flex-col gap-4 rounded-lg bg-slate-50 p-4 transition-colors hover:bg-slate-100 sm:flex-row sm:items-center"
                    >
                      <div className="flex min-w-0 items-center gap-4 flex-1">
                        <div className="w-10 h-10 rounded-full bg-[#8B1C4A] flex items-center justify-center text-white font-semibold shrink-0">
                          {u.name?.charAt(0).toUpperCase()}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-800 truncate">{u.name}</p>
                          <p className="text-sm text-slate-500 truncate">{u.email}</p>
                        </div>
                      </div>

                      <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end sm:ml-auto">
                        <Badge className={`${getRoleBadgeClass(u.role)} shrink-0`}>
                          {getRoleLabel(u.role)}
                        </Badge>

                        {u.id !== user?.id && (
                          <div className="ml-auto flex items-center justify-end gap-1 sm:gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={() => {
                                setSelectedUser(u);
                                setNewRole(u.role);
                                setNewDependency(u.dependency_id ? String(u.dependency_id) : '');
                                setEditUserOpen(true);
                              }}
                              title="Editar usuario"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>

                            <Switch
                              checked={u.status}
                              onCheckedChange={() => handleToggleUserStatus(u.id, u.status)}
                              title={u.status ? 'Desactivar usuario' : 'Activar usuario'}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
              <DialogContent className="w-[calc(100vw-32px)] max-w-[620px] max-h-[90vh] overflow-y-auto p-0">
                <DialogHeader className="px-5 pt-5 pb-2 sm:px-6 pr-12">
                  <DialogTitle className="text-center sm:text-left leading-tight break-words">
                    Editar Usuario
                  </DialogTitle>
                  <DialogDescription className="text-center sm:text-left">
                    Modifica el rol o la dependencia del usuario.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 px-5 pb-5 sm:px-6 min-w-0 max-w-full overflow-x-hidden">
                  <div className="space-y-2">
                    <Label>Rol</Label>
                    <Select value={newRole} onValueChange={setNewRole}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                        <SelectItem value="user">Usuario</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 min-w-0">
                    <Label>
                      Dependencia {newRole !== 'user' ? '*' : ''}
                    </Label>
                    <Select value={newDependency} onValueChange={setNewDependency}>
                      <SelectTrigger className="w-full min-w-0 max-w-full overflow-hidden pr-8">
                        <TruncatedSelectText placeholder="Seleccionar dependencia">
                          {newDependency
                            ? getDependencyLabelById(dependencies, newDependency)
                            : ''}
                        </TruncatedSelectText>
                      </SelectTrigger>

                      <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[92vw] sm:max-w-[560px]">
                        {dependencies.map((d) => (
                          <SelectItem
                            key={String(d.id)}
                            value={String(d.id)}
                            className="max-w-full"
                            title={`${d.acronym} - ${d.name}`}
                          >
                            <span className="block w-full whitespace-normal break-words leading-snug">
                              {d.acronym} - {d.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setEditUserOpen(false)}
                      className="w-full sm:w-auto"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleUpdateUserRole}
                      className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                    >
                      Guardar
                    </Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="activities">
            <Card className="border border-[#D4C5A5]/30 overflow-hidden">
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle>Gestión de Actividades ({activities.length})</CardTitle>
                    <Button
                      className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                      onClick={() => navigate('/cargar-actividad')}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Registrar Actividad
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-3 min-w-0 md:grid-cols-3">
                    <div className="relative min-w-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={activitySearch}
                        onChange={(e) => setActivitySearch(e.target.value)}
                        placeholder="Buscar por título, dependencia o territorio..."
                        className="pl-10 w-full min-w-0"
                      />
                    </div>

                    <div className="min-w-0">
                      <Select value={activityDependencyFilter} onValueChange={setActivityDependencyFilter}>
                        <SelectTrigger className="w-full min-w-0 max-w-full overflow-hidden">
                          <TruncatedSelectText placeholder="Todas las dependencias">
                            {activityDependencyFilter === 'all'
                              ? 'Todas las dependencias'
                              : getDependencyLabelById(dependencies, activityDependencyFilter)}
                          </TruncatedSelectText>
                        </SelectTrigger>

                        <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[92vw] sm:max-w-[560px]">
                          <SelectItem value="all">Todas las dependencias</SelectItem>
                          {dependencies.map((d) => (
                            <SelectItem
                              key={String(d.id)}
                              value={String(d.id)}
                              className="max-w-full"
                              title={`${d.acronym} - ${d.name}`}
                            >
                              <span className="block w-full whitespace-normal break-words leading-snug">
                                {d.acronym} - {d.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="min-w-0">
                      <Select value={activityTerritoryFilter} onValueChange={setActivityTerritoryFilter}>
                        <SelectTrigger className="w-full min-w-0 max-w-full overflow-hidden">
                          <TruncatedSelectText placeholder="Todos los territorios">
                            {activityTerritoryFilter === 'all'
                              ? 'Todos los territorios'
                              : getTerritoryLabelById(territories, activityTerritoryFilter)}
                          </TruncatedSelectText>
                        </SelectTrigger>

                        <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[92vw] sm:max-w-[420px]">
                          <SelectItem value="all">Todos los territorios</SelectItem>
                          {territories.map((t) => (
                            <SelectItem
                              key={String(t.id)}
                              value={String(t.id)}
                              className="max-w-full"
                              title={t.name}
                            >
                              <span className="block w-full whitespace-normal break-words leading-snug">
                                {t.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="min-w-0 overflow-x-hidden">
  {filteredActivities.length === 0 ? (
    <div className="text-center py-10 text-slate-500">
      No se encontraron actividades con esos filtros.
    </div>
  ) : (
    <div className="space-y-3">
      {filteredActivities.map((activity) => {
        const dependencyName = getDependencyName(activity);
        const territoryName = getTerritoryName(activity);
        const activityDate =
          formatDate(
            activity.start_date ||
              activity.week_start ||
              activity.created_at?.slice(0, 10)
          ) || '—';

        return (
          <div
            key={activity.id}
            className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4"
          >
            <div className="min-w-0">
              <button
                type="button"
                className="text-left"
                onClick={() => navigate(`/actividad/${activity.id}`)}
              >
                <p className="font-semibold text-slate-800 hover:text-[#8B1C4A] transition-colors">
                  {activity.title || 'Actividad sin título'}
                </p>
              </button>

              <div className="mt-2 space-y-1 text-sm text-slate-600">
                <p>
                  <span className="font-medium">Dependencia:</span> {dependencyName}
                </p>
                <p>
                  <span className="font-medium">Territorio:</span> {territoryName}
                </p>
                <p className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" />
                  {activityDate}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/actividad/${activity.id}`)}
              >
                Ver
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleDeleteActivity(activity.id, activity.title)}
                title="Eliminar actividad"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  )}
</CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="axes">
            <Card className="border border-[#D4C5A5]/30 overflow-hidden">
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle>Gestión de Ejes ({axes.length})</CardTitle>

                    <Dialog open={addAxisOpen} onOpenChange={setAddAxisOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto">
                          <Plus className="w-4 h-4 mr-2" />
                          Crear Eje
                        </Button>
                      </DialogTrigger>

                      <DialogContent className="w-[calc(100vw-32px)] max-w-[620px] max-h-[90vh] overflow-y-auto p-0">
                        <DialogHeader className="px-5 pt-5 pb-2 sm:px-6">
                          <DialogTitle>Nuevo Eje</DialogTitle>
                          <DialogDescription>
                            Crea una línea estratégica o una sublínea / línea de acción.
                          </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 px-5 pb-5 sm:px-6">
                          <div className="space-y-2">
                            <Label>Tipo *</Label>
                            <Select
                              value={newAxisData.type}
                              onValueChange={(value) =>
                                setNewAxisData({
                                  ...newAxisData,
                                  type: value,
                                  parent_id: value === 'strategic_line' ? '' : newAxisData.parent_id
                                })
                              }
                            >
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="strategic_line">Línea estratégica</SelectItem>
                                <SelectItem value="action_line">Sublínea / línea de acción</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {newAxisData.type === 'action_line' && (
                            <div className="space-y-2">
                              <Label>Línea estratégica padre *</Label>
                              <Select
                                value={newAxisData.parent_id}
                                onValueChange={(value) =>
                                  setNewAxisData({ ...newAxisData, parent_id: value })
                                }
                              >
                                <SelectTrigger><SelectValue placeholder="Selecciona línea estratégica" /></SelectTrigger>
                                <SelectContent>
                                  {strategicAxes.map((axis) => (
                                    <SelectItem key={String(axis.id)} value={String(axis.id)}>
                                      {axis.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          <div className="space-y-2">
                            <Label>Nombre *</Label>
                            <Input
                              value={newAxisData.name}
                              onChange={(e) => setNewAxisData({ ...newAxisData, name: e.target.value })}
                              placeholder="Ej: Trabajo y justicia social"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Descripción</Label>
                            <Textarea
                              value={newAxisData.description}
                              onChange={(e) => setNewAxisData({ ...newAxisData, description: e.target.value })}
                              rows={4}
                              placeholder="Describe brevemente este eje o línea de acción..."
                            />
                          </div>

                          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
                            <Button variant="outline" onClick={() => setAddAxisOpen(false)} className="w-full sm:w-auto">
                              Cancelar
                            </Button>
                            <Button
                              onClick={handleAddAxis}
                              className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                              disabled={!canCreateAxis}
                            >
                              Crear Eje
                            </Button>
                          </DialogFooter>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={axisSearch}
                      onChange={(e) => setAxisSearch(e.target.value)}
                      placeholder="Buscar eje o subeje..."
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>

              <CardContent className="min-w-0 overflow-x-hidden">
                {filteredAxesWithChildren.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    No se encontraron ejes.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredAxesWithChildren.map((axis) => {
                      const isOpen = !!expandedAxes[axis.id];

                      return (
                        <div
                          key={axis.id}
                          className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <Badge className="bg-[#8B1C4A] text-white">
                                  Línea estratégica
                                </Badge>

                                <Badge variant="outline">
                                  {axis.children.length} subeje{axis.children.length === 1 ? '' : 's'}
                                </Badge>
                              </div>

                              <p className="font-semibold text-slate-800">{axis.name}</p>

                              {axis.description ? (
                                <p className="text-sm text-slate-600 mt-1">{axis.description}</p>
                              ) : null}
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toggleAxis(axis.id)}
                              >
                                {isOpen ? 'Ocultar subejes' : 'Ver subejes'}
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteAxis(axis.id, 'strategic_line')}
                                title="Eliminar eje"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {isOpen ? (
                            <div className="mt-4 space-y-3 border-t pt-4">
                              {axis.children.length === 0 ? (
                                <p className="text-sm text-slate-500">
                                  Este eje aún no tiene subejes.
                                </p>
                              ) : (
                                axis.children.map((subaxis) => (
                                  <div
                                    key={subaxis.id}
                                    className="rounded-md border border-slate-200 bg-white p-3 flex items-start justify-between gap-3"
                                  >
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <Badge className="bg-[#BC955C] text-black">
                                          Sublínea
                                        </Badge>
                                      </div>

                                      <p className="font-medium text-slate-800">{subaxis.name}</p>

                                      {subaxis.description ? (
                                        <p className="text-sm text-slate-600 mt-1">
                                          {subaxis.description}
                                        </p>
                                      ) : null}
                                    </div>

                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                                      onClick={() => handleDeleteAxis(subaxis.id, 'action_line')}
                                      title="Eliminar subeje"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="territories">
            <Card className="border border-[#D4C5A5]/30">
              <CardHeader>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle>Gestión de Territorios ({filteredTerritories.length})</CardTitle>

                  <Dialog open={addTerritoryOpen} onOpenChange={setAddTerritoryOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#8B1C4A] hover:bg-[#70163A]">
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Territorio
                      </Button>
                    </DialogTrigger>

                    <DialogContent className="w-[calc(100vw-32px)] max-w-[620px] max-h-[90vh] overflow-y-auto p-0">
                      <DialogHeader className="px-5 pt-5 pb-2 sm:px-6">
                        <DialogTitle>Nuevo Territorio</DialogTitle>
                        <DialogDescription>
                          Crea un territorio y asígnale una alcaldía.
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-4 px-5 pb-5 sm:px-6">
                        <div className="space-y-2">
                          <Label>Nombre *</Label>
                          <Input
                            value={newTerritory.name}
                            onChange={(e) =>
                              setNewTerritory({
                                ...newTerritory,
                                name: e.target.value.replace(/\s{2,}/g, ' ')
                              })
                            }
                            placeholder="Ej: Barrio Norte"
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Código *</Label>
                            <Input
                              value={newTerritory.code}
                              onChange={(e) =>
                                setNewTerritory({
                                  ...newTerritory,
                                  code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 10)
                                })
                              }
                              placeholder="Ej: AOB-1"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Alcaldía *</Label>
                            <Select
                              value={newTerritory.alcaldia_id}
                              onValueChange={(value) =>
                                setNewTerritory({ ...newTerritory, alcaldia_id: value })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecciona una alcaldía" />
                              </SelectTrigger>
                              <SelectContent>
                                {alcaldias.map((alcaldia) => (
                                  <SelectItem key={String(alcaldia.id)} value={String(alcaldia.id)}>
                                    {alcaldia.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Latitud *</Label>
                            <Input
                              type="number"
                              step="0.0001"
                              value={newTerritory.lat}
                              onChange={(e) => setNewTerritory({ ...newTerritory, lat: e.target.value })}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Longitud *</Label>
                            <Input
                              type="number"
                              step="0.0001"
                              value={newTerritory.lng}
                              onChange={(e) => setNewTerritory({ ...newTerritory, lng: e.target.value })}
                            />
                          </div>
                        </div>

                        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
                          <Button variant="outline" onClick={() => setAddTerritoryOpen(false)} className="w-full sm:w-auto">
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleAddTerritory}
                            className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                            disabled={!canCreateTerritory}
                          >
                            Crear
                          </Button>
                        </DialogFooter>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={territorySearch}
                      onChange={(e) => setTerritorySearch(e.target.value)}
                      placeholder="Buscar por nombre, código o alcaldía"
                      className="pl-10"
                    />
                  </div>

                  <Select value={territoryAlcaldiaFilter} onValueChange={setTerritoryAlcaldiaFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filtrar por alcaldía" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las alcaldías</SelectItem>
                      {alcaldias.map((alcaldia) => (
                        <SelectItem key={String(alcaldia.id)} value={String(alcaldia.id)}>
                          {alcaldia.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

              </CardHeader>

              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredTerritories.map((t) => (
                    <div key={t.id} className="p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-800">{t.name}</p>
                          <p className="text-sm text-slate-500">
                            {t.alcaldia || alcaldias.find((a) => String(a.id) === String(t.alcaldia_id))?.name || '—'}
                          </p>
                          <Badge variant="outline" className="mt-2">{t.code}</Badge>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteTerritory(t.id)}
                          title="Eliminar territorio"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="polygons">
            <Card className="border border-[#D4C5A5]/30">
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle>Gestión de Polígonos ({polygons.length})</CardTitle>

                    <Dialog open={addPolygonOpen} onOpenChange={setAddPolygonOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#8B1C4A] hover:bg-[#70163A]">
                          <Plus className="w-4 h-4 mr-2" />
                          Agregar Polígono
                        </Button>
                      </DialogTrigger>

                      <DialogContent className="w-[calc(100vw-32px)] max-w-[620px] max-h-[90vh] overflow-y-auto p-0">
                        <DialogHeader className="px-5 pt-5 pb-2 sm:px-6">
                          <DialogTitle>Nuevo Polígono</DialogTitle>
                          <DialogDescription>
                            Registra un polígono asociado a un territorio.
                          </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 px-5 pb-5 sm:px-6">
                          <div className="space-y-2">
                            <Label>Territorio *</Label>
                            <Select
                              value={newPolygonData.territory_id}
                              onValueChange={(value) => setNewPolygonData({ ...newPolygonData, territory_id: value })}
                            >
                              <SelectTrigger><SelectValue placeholder="Selecciona un territorio" /></SelectTrigger>
                              <SelectContent>
                                {territories.map((t) => (
                                  <SelectItem key={String(t.id)} value={String(t.id)}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label>Nombre *</Label>
                            <Input
                              value={newPolygonData.name}
                              onChange={(e) =>
                                setNewPolygonData({ ...newPolygonData, name: e.target.value })
                              }
                              placeholder="Ej: Polígono San Pedro Xalpa"
                            />
                          </div>

                          {/* Pendiente por validar nuevamente con backend:
<div className="space-y-2">
  <Label>GeoJSON *</Label>
  <Textarea
    value={newPolygonData.geojson}
    onChange={(e) => setNewPolygonData({ ...newPolygonData, geojson: e.target.value })}
    rows={10}
    className="font-mono text-sm"
  />
</div>
*/}

                          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
                            <Button variant="outline" onClick={() => setAddPolygonOpen(false)} className="w-full sm:w-auto">
                              Cancelar
                            </Button>
                            <Button
                              onClick={handleAddPolygon}
                              className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                              disabled={!canCreatePolygon}
                            >
                              Crear Polígono
                            </Button>
                          </DialogFooter>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={polygonSearch}
                        onChange={(e) => setPolygonSearch(e.target.value)}
                        placeholder="Buscar por nombre, id o territorio"
                        className="pl-10"
                      />
                    </div>

                    <Select value={polygonTerritoryFilter} onValueChange={setPolygonTerritoryFilter}>
                      <SelectTrigger><SelectValue placeholder="Filtrar por territorio" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los territorios</SelectItem>
                        {territories.map((t) => (
                          <SelectItem key={String(t.id)} value={String(t.id)}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                {filteredPolygons.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">No se encontraron polígonos.</div>
                ) : (
                  <div className="space-y-3">
                    {filteredPolygons.map((polygon) => {
                      const territory = territories.find((t) => String(t.id) === String(polygon.territory_id));
                      return (
                        <div
                          key={polygon.id}
                          className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-start justify-between gap-4"
                        >
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800">
                              {polygon.name || `Polígono ${polygon.id?.slice(0, 8) || 'sin id'}`}
                            </p>
                            <p className="text-sm text-slate-600 mt-1">
                              <span className="font-medium">Territorio:</span> {territory?.name || '—'}
                            </p>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeletePolygon(polygon.id)}
                            title="Eliminar polígono"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dependencies">
            <Card className="border border-[#D4C5A5]/30">
              <CardHeader>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle>Gestión de Dependencias ({dependencies.length})</CardTitle>

                  <Dialog open={addDependencyOpen} onOpenChange={setAddDependencyOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#8B1C4A] hover:bg-[#70163A]">
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Dependencia
                      </Button>
                    </DialogTrigger>

                    <DialogContent className="w-[calc(100vw-32px)] max-w-[620px] max-h-[90vh] overflow-y-auto p-0">
                      <DialogHeader className="px-5 pt-5 pb-2 sm:px-6">
                        <DialogTitle>Nueva Dependencia</DialogTitle>
                        <DialogDescription>Registra una nueva dependencia.</DialogDescription>
                      </DialogHeader>

                      <div className="space-y-4 px-5 pb-5 sm:px-6">
                        <div className="space-y-2">
                          <Label>Nombre *</Label>
                          <Input
                            value={newDependencyData.name}
                            onChange={(e) =>
                              setNewDependencyData({
                                ...newDependencyData,
                                name: e.target.value.replace(/\s{2,}/g, ' ')
                              })
                            }
                            placeholder="Ej: Secretaría de Salud"
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Acrónimo *</Label>
                            <Input
                              value={newDependencyData.acronym}
                              onChange={(e) =>
                                setNewDependencyData({
                                  ...newDependencyData,
                                  acronym: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10)
                                })
                              }
                              placeholder="Ej: SEDESA"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Color *</Label>
                            <Input
                              type="color"
                              value={newDependencyData.color}
                              onChange={(e) => setNewDependencyData({ ...newDependencyData, color: e.target.value })}
                            />
                          </div>
                        </div>

                        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
                          <Button variant="outline" onClick={() => setAddDependencyOpen(false)} className="w-full sm:w-auto">
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleAddDependency}
                            className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                            disabled={!canCreateDependency}
                          >
                            Crear
                          </Button>
                        </DialogFooter>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>

              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  {dependencies.map((d) => (
                    <div key={d.id} className="p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="w-4 h-4 rounded-full mt-1 shrink-0" style={{ backgroundColor: d.color }} />
                          <div className="min-w-0">
                            <Badge variant="secondary" className="mb-1">{d.acronym}</Badge>
                            <p className="font-medium text-slate-800">{d.name}</p>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteDependency(d.id)}
                          title="Eliminar dependencia"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;